import {
    House,
    Laptop,
    Leaf,
    Shirt,
    ShoppingBag,
    ShoppingBasket,
    Smartphone,
    Sparkles,
    Store,
    ToyBrick,
    UtensilsCrossed,
    Zap,
} from "lucide-react";
import { ReactNode } from "react";

export type MobileHomeServiceId =
    | "shopping"
    | "quick"
    | "food"
    | "stores";

export type MobileHomeCategoryId =
    | "men"
    | "women"
    | "fresh"
    | "groceries"
    | "kids"
    | "beauty"
    | "mobiles"
    | "electronics"
    | "home";

export type MobileHomeService = {
    id: MobileHomeServiceId;
    label: string;
    caption: string;
    icon: ReactNode;
};

export type MobileCategoryOffer = {
    id: string;
    title: string;
    caption: string;
    badge: string;
    emoji: string;
};

export type MobileHomeCategory = {
    id: MobileHomeCategoryId;
    label: string;
    shortLabel: string;
    icon: ReactNode;
    eyebrow: string;
    title: string;
    subtitle: string;
    themeClass: string;
    offers: MobileCategoryOffer[];
};

export const mobileHomeServices: MobileHomeService[] = [
    {
        id: "shopping",
        label: "Salar Shopping",
        caption: "Marketplace",
        icon: <ShoppingBag size={19} strokeWidth={1.9} />,
    },
    {
        id: "quick",
        label: "Quick Minutes",
        caption: "Fast delivery",
        icon: <Zap size={19} strokeWidth={2} />,
    },
    {
        id: "food",
        label: "Food",
        caption: "Salar Eats",
        icon: <UtensilsCrossed size={19} strokeWidth={1.9} />,
    },
    {
        id: "stores",
        label: "Stores Nearby",
        caption: "Around you",
        icon: <Store size={19} strokeWidth={1.9} />,
    },
];

export const mobileHomeCategories: MobileHomeCategory[] = [
    {
        id: "men",
        label: "Men Fashion",
        shortLabel: "Men",
        icon: <Shirt size={21} strokeWidth={1.8} />,
        eyebrow: "MEN'S EDIT",
        title: "Everyday fits, upgraded.",
        subtitle:
            "Fresh shirts, sneakers and essentials picked for your day.",
        themeClass: "theme-men",
        offers: [
            {
                id: "men-linen",
                title: "Linen Edit",
                caption: "Lightweight styles",
                badge: "From ₹899",
                emoji: "👔",
            },
            {
                id: "men-shoes",
                title: "Sneaker Drop",
                caption: "New-season pairs",
                badge: "Up to 40% off",
                emoji: "👟",
            },
            {
                id: "men-tees",
                title: "Daily Tees",
                caption: "Easy everyday picks",
                badge: "2 from ₹699",
                emoji: "👕",
            },
        ],
    },
    {
        id: "women",
        label: "Women Fashion",
        shortLabel: "Women",
        icon: <Sparkles size={21} strokeWidth={1.8} />,
        eyebrow: "WOMEN'S EDIT",
        title: "A little more style today.",
        subtitle:
            "Dresses, bags and footwear with a polished everyday feel.",
        themeClass: "theme-women",
        offers: [
            {
                id: "women-dresses",
                title: "Dress Studio",
                caption: "New silhouettes",
                badge: "From ₹999",
                emoji: "👗",
            },
            {
                id: "women-bags",
                title: "Bag Edit",
                caption: "Carry it your way",
                badge: "Up to 35% off",
                emoji: "👜",
            },
            {
                id: "women-shoes",
                title: "Footwear",
                caption: "Work to weekend",
                badge: "New arrivals",
                emoji: "👠",
            },
        ],
    },
    {
        id: "fresh",
        label: "Fresh",
        shortLabel: "Fresh",
        icon: <Leaf size={21} strokeWidth={1.8} />,
        eyebrow: "FRESH TODAY",
        title: "From market to your kitchen.",
        subtitle:
            "Fruits, vegetables and everyday fresh picks for quick delivery.",
        themeClass: "theme-fresh",
        offers: [
            {
                id: "fresh-fruit",
                title: "Fruit Basket",
                caption: "Seasonal favourites",
                badge: "Fresh picks",
                emoji: "🍎",
            },
            {
                id: "fresh-veg",
                title: "Daily Veggies",
                caption: "Kitchen staples",
                badge: "Save 15%",
                emoji: "🥬",
            },
            {
                id: "fresh-dairy",
                title: "Dairy Corner",
                caption: "Milk & breakfast",
                badge: "Morning deal",
                emoji: "🥛",
            },
        ],
    },
    {
        id: "groceries",
        label: "Groceries",
        shortLabel: "Groceries",
        icon: <ShoppingBasket size={21} strokeWidth={1.8} />,
        eyebrow: "PANTRY PICKS",
        title: "Restock without the long list.",
        subtitle:
            "Rice, snacks, beverages and household staples in one quick stop.",
        themeClass: "theme-groceries",
        offers: [
            {
                id: "grocery-rice",
                title: "Kitchen Staples",
                caption: "Rice, atta & pulses",
                badge: "Up to 20% off",
                emoji: "🍚",
            },
            {
                id: "grocery-snack",
                title: "Snack Shelf",
                caption: "Tea-time favourites",
                badge: "Combo offers",
                emoji: "🍪",
            },
            {
                id: "grocery-drinks",
                title: "Cool Drinks",
                caption: "Juices & beverages",
                badge: "Chilled deals",
                emoji: "🧃",
            },
        ],
    },
    {
        id: "kids",
        label: "Kids & Toys",
        shortLabel: "Kids",
        icon: <ToyBrick size={21} strokeWidth={1.8} />,
        eyebrow: "KIDS' CORNER",
        title: "Play, learn, repeat.",
        subtitle:
            "Toys, activity kits and little essentials for curious minds.",
        themeClass: "theme-kids",
        offers: [
            {
                id: "kids-toys",
                title: "Toy Box",
                caption: "Playtime favourites",
                badge: "From ₹299",
                emoji: "🧸",
            },
            {
                id: "kids-learning",
                title: "Learn & Create",
                caption: "Activity kits",
                badge: "Top picks",
                emoji: "🎨",
            },
            {
                id: "kids-baby",
                title: "Little Care",
                caption: "Baby essentials",
                badge: "Value packs",
                emoji: "🍼",
            },
        ],
    },
    {
        id: "beauty",
        label: "Beauty & Personal",
        shortLabel: "Beauty",
        icon: <Sparkles size={21} strokeWidth={1.8} />,
        eyebrow: "BEAUTY DESK",
        title: "Your everyday reset.",
        subtitle:
            "Skincare, grooming and beauty essentials with easy-to-shop edits.",
        themeClass: "theme-beauty",
        offers: [
            {
                id: "beauty-skin",
                title: "Skin First",
                caption: "Daily skincare",
                badge: "Up to 30% off",
                emoji: "🧴",
            },
            {
                id: "beauty-makeup",
                title: "Makeup Edit",
                caption: "Everyday colour",
                badge: "Trending",
                emoji: "💄",
            },
            {
                id: "beauty-groom",
                title: "Grooming",
                caption: "Personal care",
                badge: "Best sellers",
                emoji: "🪒",
            },
        ],
    },
    {
        id: "mobiles",
        label: "Mobiles",
        shortLabel: "Mobiles",
        icon: <Smartphone size={21} strokeWidth={1.8} />,
        eyebrow: "MOBILE ZONE",
        title: "Upgrade what stays in your hand.",
        subtitle:
            "Smartphones, cases and charging essentials across every budget.",
        themeClass: "theme-mobiles",
        offers: [
            {
                id: "mobile-phones",
                title: "Phone Deals",
                caption: "Popular smartphones",
                badge: "Exchange bonus",
                emoji: "📱",
            },
            {
                id: "mobile-audio",
                title: "Audio",
                caption: "TWS & headphones",
                badge: "From ₹799",
                emoji: "🎧",
            },
            {
                id: "mobile-charge",
                title: "Power Up",
                caption: "Chargers & cables",
                badge: "Fast-charge picks",
                emoji: "🔋",
            },
        ],
    },
    {
        id: "electronics",
        label: "Electronics",
        shortLabel: "Electronics",
        icon: <Laptop size={21} strokeWidth={1.8} />,
        eyebrow: "TECH SELECT",
        title: "Smart upgrades for work and play.",
        subtitle:
            "Laptops, accessories and connected tech chosen for everyday use.",
        themeClass: "theme-electronics",
        offers: [
            {
                id: "electronics-laptop",
                title: "Laptop Store",
                caption: "Work & study",
                badge: "No-cost EMI",
                emoji: "💻",
            },
            {
                id: "electronics-watch",
                title: "Wearables",
                caption: "Smart on your wrist",
                badge: "Up to 45% off",
                emoji: "⌚",
            },
            {
                id: "electronics-speaker",
                title: "Sound Room",
                caption: "Speakers & audio",
                badge: "Weekend deal",
                emoji: "🔊",
            },
        ],
    },
    {
        id: "home",
        label: "Home & Kitchen",
        shortLabel: "Home",
        icon: <House size={21} strokeWidth={1.8} />,
        eyebrow: "HOME EDIT",
        title: "Small changes. Better spaces.",
        subtitle:
            "Kitchen helpers, décor and useful upgrades for everyday living.",
        themeClass: "theme-home",
        offers: [
            {
                id: "home-kitchen",
                title: "Kitchen Edit",
                caption: "Useful everyday tools",
                badge: "From ₹399",
                emoji: "🍳",
            },
            {
                id: "home-decor",
                title: "Décor Picks",
                caption: "Refresh your room",
                badge: "New collection",
                emoji: "🪴",
            },
            {
                id: "home-clean",
                title: "Easy Cleaning",
                caption: "Home care",
                badge: "Save up to 25%",
                emoji: "🧹",
            },
        ],
    },
];

export const defaultMobileDeliveryAddress =
    "Select delivery location";
